import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ThinkingFallacy {
  name: string;
  english: string;
  desc: string;
  icon: string;
  color: string;
}

interface ThinkingStep {
  step: string;
  desc: string;
}

interface ThinkingSkill {
  name: string;
  desc: string;
  icon: string;
}

interface CriticalThinkingTemplateProps {
  topic: string;
  subtitle: string;
  fallacies: ThinkingFallacy[];
  steps: ThinkingStep[];
  skills: ThinkingSkill[];
  logicTreeNodes: { label: string; children: string[] }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: CriticalThinkingTemplateProps = {
  topic: '批判性思维与逻辑分析',
  subtitle: 'Critical Thinking & Logical Analysis',
  fallacies: [
    {
      name: '确认偏误',
      english: 'Confirmation Bias',
      desc: '只关注支持自己观点的信息，忽略反面证据',
      icon: '🔍',
      color: '#60a5fa',
    },
    {
      name: '滑坡谬误',
      english: 'Slippery Slope',
      desc: '不合理地假设一件事必然导致极端后果',
      icon: '⛷️',
      color: '#f472b6',
    },
    {
      name: '稻草人谬误',
      english: 'Straw Man',
      desc: '歪曲对方观点后再加以攻击',
      icon: '🌾',
      color: '#fbbf24',
    },
    {
      name: '诉诸权威',
      english: 'Appeal to Authority',
      desc: '仅因权威人士说过就认定观点正确',
      icon: '👔',
      color: '#34d399',
    },
  ],
  steps: [
    { step: '提出质疑', desc: '对信息来源和假设保持怀疑' },
    { step: '收集证据', desc: '系统性地搜集多方面证据' },
    { step: '分析推理', desc: '运用逻辑方法评估证据' },
    { step: '得出结论', desc: '基于证据形成合理判断' },
  ],
  skills: [
    { name: '归纳推理', desc: '从特殊到一般', icon: '📊' },
    { name: '演绎推理', desc: '从一般到特殊', icon: '🔬' },
    { name: '类比推理', desc: '基于相似性推断', icon: '🔗' },
    { name: '反证法', desc: '假设反面以证明命题', icon: '🔄' },
  ],
  logicTreeNodes: [
    { label: '核心论点', children: ['前提A', '前提B'] },
    { label: '前提A', children: ['证据A1', '证据A2'] },
    { label: '前提B', children: ['证据B1', '证据B2'] },
  ],
  stats: [
    { value: '72%', label: '成年人存在确认偏误倾向' },
    { value: '200+', label: '已识别的认知偏差种类' },
    { value: '40%', label: '决策受情感而非逻辑驱动' },
  ],
  bottomQuote:
    '真正的智慧不是知道所有答案，而是学会提出正确的问题——苏格拉底式追问是批判性思维的起点',
};

export const CriticalThinkingTemplate: React.FC<
  Partial<CriticalThinkingTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const fallacyStart = 30;
  const stepsStart = 160;
  const treeStart = 200;
  const skillStart = 350;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const treeDraw = interpolate(frame, [treeStart, treeStart + 120], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0c1a2e 0%, #0a1628 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating question mark particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 397) % 1920;
        const y = (i * 223) % 1080;
        const size = 14 + (i % 8) * 2;
        const drift = Math.sin(frame * 0.025 + i * 1.1) * 10;
        const driftX = Math.cos(frame * 0.02 + i * 0.8) * 6;
        const pulse = 0.06 + 0.04 * Math.sin(frame * 0.04 + i * 0.9);
        const colors = ['#60a5fa', '#93c5fd', '#dbeafe', '#3b82f6'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              fontSize: size,
              opacity: pulse,
              color: colors[i % colors.length],
              textShadow: `0 0 ${size}px ${colors[i % colors.length]}30`,
            }}
          >
            ?
          </div>
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#1e3a5f',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#60a5fa',
            flexShrink: 0,
          }}
        >
          🧠 思维科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#dbeafe',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Thinking Fallacies ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 460 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          常见逻辑谬误
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.fallacies.map((f, i) => {
            const s = spring({
              frame: frame - (fallacyStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#0f172a',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${f.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 60,
                    height: 52,
                    borderRadius: '50%',
                    background: `${f.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${f.color}30`,
                  }}
                >
                  {f.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 24, fontWeight: 800, color: f.color }}>
                      {f.name}
                    </span>
                    <span style={{ fontSize: 24, color: '#64748b' }}>
                      {f.english}
                    </span>
                  </div>
                  <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                    {f.desc}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Critical Thinking Steps */}
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            批判性思维四步法
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {p.steps.map((st, i) => {
              const s = spring({
                frame: frame - (stepsStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <React.Fragment key={i}>
                  <div
                    style={{
                      flex: 1,
                      background: '#0f172a',
                      borderRadius: 12,
                      padding: '14px 10px',
                      textAlign: 'center',
                      border: '1.5px solid #1e3a5f',
                      opacity: s,
                      transform: `translateY(${(1 - s) * 16}px)`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        color: '#60a5fa',
                        fontWeight: 700,
                        marginBottom: 4,
                      }}
                    >
                      Step {i + 1}
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: '#dbeafe',
                        marginBottom: 4,
                      }}
                    >
                      {st.step}
                    </div>
                    <div style={{ fontSize: 17, color: '#94a3b8' }}>{st.desc}</div>
                  </div>
                  {i < p.steps.length - 1 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#60a5fa',
                        fontSize: 20,
                        opacity: s,
                      }}
                    >
                      →
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Logic Tree SVG ===== */}
      <div style={{ position: 'absolute', top: 120, left: 700, width: 650 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 12,
            letterSpacing: '0.08em',
          }}
        >
          逻辑论证树
        </div>
        <svg width="620" height="470" viewBox="0 0 620 470">
          {/* Tree structure */}
          <g opacity={treeDraw}>
            {/* Root node */}
            <rect x="215" y="10" width="190" height="60" rx="14" fill="#1e3a5f" stroke="#60a5fa" strokeWidth="2.5" />
            <text x="310" y="48" fill="#dbeafe" fontSize="24" fontWeight="700" textAnchor="middle">
              核心论点
            </text>

            {/* Lines from root to level 1 */}
            <line x1="265" y1="70" x2="155" y2="125" stroke="#60a5fa" strokeWidth="2.5" opacity={treeDraw} />
            <line x1="355" y1="70" x2="465" y2="125" stroke="#60a5fa" strokeWidth="2.5" opacity={treeDraw} />

            {/* Level 1 nodes */}
            <rect x="65" y="125" width="180" height="55" rx="12" fill="#0f172a" stroke="#3b82f6" strokeWidth="2" />
            <text x="155" y="160" fill="#93c5fd" fontSize="24" fontWeight="700" textAnchor="middle">
              前提 A
            </text>

            <rect x="375" y="125" width="180" height="55" rx="12" fill="#0f172a" stroke="#3b82f6" strokeWidth="2" />
            <text x="465" y="160" fill="#93c5fd" fontSize="24" fontWeight="700" textAnchor="middle">
              前提 B
            </text>

            {/* Lines from level 1 to level 2 */}
            <line x1="110" y1="180" x2="72" y2="230" stroke="#3b82f6" strokeWidth="2" opacity={treeDraw} />
            <line x1="200" y1="180" x2="240" y2="230" stroke="#3b82f6" strokeWidth="2" opacity={treeDraw} />
            <line x1="420" y1="180" x2="385" y2="230" stroke="#3b82f6" strokeWidth="2" opacity={treeDraw} />
            <line x1="510" y1="180" x2="545" y2="230" stroke="#3b82f6" strokeWidth="2" opacity={treeDraw} />

            {/* Level 2 nodes */}
            {['证据A1', '证据A2', '证据B1', '证据B2'].map((label, i) => {
              const xPos = [10, 178, 323, 483][i];
              const nodeS = spring({
                frame: frame - (treeStart + 50 + i * 12),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <g key={i} opacity={nodeS}>
                  <rect x={xPos} y="230" width="125" height="50" rx="10" fill="#0f172a" stroke="#60a5fa50" strokeWidth="1.5" />
                  <text x={xPos + 62} y="265" fill="#94a3b8" fontSize="22" fontWeight="600" textAnchor="middle">
                    {label}
                  </text>
                </g>
              );
            })}

            {/* Validity indicators */}
            {['✓ 有效', '✗ 薄弱', '✓ 有效', '? 待验证'].map((label, i) => {
              const xPos = [35, 203, 348, 508][i];
              const colors = ['#34d399', '#f87171', '#34d399', '#fbbf24'];
              const indicS = spring({
                frame: frame - (treeStart + 80 + i * 10),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <text
                  key={`v-${i}`}
                  x={xPos + 38}
                  y="305"
                  fill={colors[i]}
                  fontSize="20"
                  fontWeight="700"
                  textAnchor="middle"
                  opacity={indicS}
                >
                  {label}
                </text>
              );
            })}
          </g>

          {/* Annotation */}
          <g
            opacity={interpolate(frame, [treeStart + 100, treeStart + 120], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            <rect x="110" y="335" width="400" height="115" rx="14" fill="#1e3a5f20" stroke="#60a5fa30" strokeWidth="1.5" />
            <text x="310" y="368" fill="#94a3b8" fontSize="24" textAnchor="middle">
              论证评估要点
            </text>
            <text x="310" y="398" fill="#dbeafe" fontSize="20" textAnchor="middle">
              • 前提是否为真？ • 推理是否有效？
            </text>
            <text x="310" y="427" fill="#dbeafe" fontSize="20" textAnchor="middle">
              • 是否存在隐含假设？ • 结论是否可靠？
            </text>
          </g>
        </svg>
      </div>

      {/* ===== RIGHT COLUMN: Key Skills ===== */}
      <div style={{ position: 'absolute', top: 120, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          核心推理技能
        </div>
        {p.skills.map((sk, i) => {
          const s = spring({
            frame: frame - (skillStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f172a',
                borderRadius: 12,
                padding: '14px 16px',
                border: '1.5px solid #1e3a5f',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 10,
                  background: '#1e3a5f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  flexShrink: 0,
                }}
              >
                {sk.icon}
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#60a5fa' }}>
                  {sk.name}
                </div>
                <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                  {sk.desc}
                </div>
              </div>
            </div>
          );
        })}

        {/* Cognitive Bias Awareness */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            认知偏差防范
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {[
              { label: '多角度思考', icon: '🔀' },
              { label: '质疑假设', icon: '❓' },
              { label: '数据验证', icon: '📈' },
              { label: '逆向思维', icon: '🔃' },
            ].map((item, i) => {
              const s = spring({
                frame: frame - (skillStart + 80 + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0f172a',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #1e3a5f',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 4 }}>{item.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#60a5fa' }}>
                    {item.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f172a',
                borderRadius: 14,
                padding: '14px 18px',
                border: '1px solid #1e3a5f',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <div style={{ fontSize: 34, fontWeight: 900, color: '#60a5fa', flexShrink: 0 }}>
                {st.value}
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8' }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#0f172a',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #1e3a5f',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>💡</span>
          <span style={{ fontSize: 20, color: '#dbeafe', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #1e3a5f, #60a5fa, #dbeafe)',
        }}
      />
    </AbsoluteFill>
  );
};
